import { useQuery, useLazyQuery } from '@apollo/client/react';
import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { formatDateToUtc } from '../../../utils/helpers/datetimeHelper';
import {
    GET_CARRIERS,
    GET_ALL_REGIONS,
    GET_DRIVERCOMPANYREFERENCE,
    GET_DRIVER_BY_CELLPHONE,
    GET_ALL_DRIVERS_MASTER_DATA,
} from '../../../graphql/client/queries';
import {
    GetAllDriversMasterData_getAllDriversMasterData as Driver,
    GetAllDriversMasterData,
    GetCarrier_getCarriers as CarrierType,
    GetCarrier,
    GetAllRegions,
    GetAllRegions_getAllRegions as RegionType,
    GetAllRegions_getAllRegions_districts as DistrictType,
    GetDriverByCellPhone,
    GetDriverCompanyReferenceByDriver,
    GetDriverCompanyReferenceByDriver_getDriverCompanyReferenceByDriver as DriverCompanyReferenceType,
} from '../../../types/schemaTypes';
import { CustomButton, DateTimePicker, Loading } from '../../_common';
import { Textfield } from '../../_common/Textfield/Textfield';
import { ModalForm } from '../interface';
import './AddEditDriverModal.scss';
import { MultiSelectDropdownV2 } from '../../_common/MultiSelectDropdown/MultiSelectDropdownV2';
import SearchableDropdown from '../../_common/SearchableDropdown/SearchableDropdown';
// import { ValueType } from 'react-select';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import DriverCompanyReferenceItem from '../DriverCompanyReferenceItem';
import moment from 'moment';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UpdateDriverConfirmationModal from '../UpdateDriverConfirmationModal/UpdateDriverConfirmationModal';

interface Option {
    id: number | string;
    name: string | number;
}

interface OptionType {
    label: string;
    value: string;
}

interface CarrierOption {
    id: number | string;
    companyName: string;
    companyType: string;
    acronym: string;
}

interface DriverCompanies {
    id: number;
    companyName: string;
    updatedDate: string;
    updatedBy: string;
}

/** Accepts typed masked input, pasted 10 digits, (NNN) NNN-NNNN, +1…, etc. */
function normalizeUsCellPhone(phone: string): string | null {
    if (phone == null || typeof phone !== 'string') return null;
    const digits = phone.replace(/\D/g, '');
    const ten = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
    if (ten.length !== 10) return null;
    return `${ten.slice(0, 3)}-${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/**
 * Detects the trailing acronym on a driver name.
 * - If the name ends with a space + the tracked acronym, returns that acronym.
 * - Otherwise, if the last whitespace-separated token looks like an acronym
 *   (2-6 alphanumerics, all uppercase), returns that token uppercased.
 * - Otherwise returns ''.
 */
function detectTrailingAcronym(name: string, trackedAcronym: string): string {
    const trimmed = (name ?? '').trim();
    const tracked = (trackedAcronym ?? '').trim().toUpperCase();
    if (tracked.length > 0 && trimmed.toUpperCase().endsWith(` ${tracked}`)) return tracked;
    const parts = trimmed.split(/\s+/);
    if (parts.length <= 1) return '';
    const lastToken = parts[parts.length - 1] ?? '';
    if (/^[A-Z0-9]{2,6}$/.test(lastToken)) return lastToken.toUpperCase();
    return '';
}

/**
 * Replaces the trailing carrier-acronym suffix on a driver name.
 * - Strips the previously applied acronym (if the name ends with a space + old acronym).
 * - Appends the new acronym as a space-separated suffix (uppercased).
 * - If the new acronym is empty, the name is returned without any suffix.
 */
function replaceDriverNameSuffix(name: string, oldAcronym: string, newAcronym: string): string {
    const trimmedName = (name ?? '').trim();
    const trimmedOld = (oldAcronym ?? '').trim().toUpperCase();
    const trimmedNew = (newAcronym ?? '').trim().toUpperCase();

    let baseName = trimmedName;
    if (trimmedOld.length > 0) {
        const suffix = ` ${trimmedOld}`;
        if (baseName.toUpperCase().endsWith(suffix)) {
            baseName = baseName.slice(0, baseName.length - suffix.length).trimEnd();
        }
    }

    if (trimmedNew.length === 0) return baseName;
    if (baseName.length === 0) return '';
    if (baseName.toUpperCase().endsWith(` ${trimmedNew}`)) return baseName;
    return `${baseName} ${trimmedNew}`;
}

interface AddEditDriverModalProps {
    modalForm: ModalForm;
    options: Option[];
    driverId?: number;
    onClickHide: () => void;
    onClickSave: (
        id: number,
        driverName: string,
        cellPhone: string,
        cdlNumber: string,
        cdlExpiryDate: Date,
        kronosId: string,
        isActive: boolean,
        isAssigned: boolean,
        isManual: boolean,
        isAvailable: boolean,
        isShiftStarted: boolean,
        isLocked: boolean,
        driverCompanyId: number,
        extSystemName: string,
        extSystemIdentifier: string,
        activeInactiveDate: Date,
        inactiveReason: string,
        reactivationDate: Date,
        regionIds: string,
        districtIds: string,
        extSystemIdentifier3: string,
    ) => void;
    show: boolean;
    errorMessage?: string;
}

const AddEditDriverModal = ({
    show,
    options,
    driverId,
    onClickHide,
    onClickSave,
    modalForm,
    errorMessage,
}: AddEditDriverModalProps): React.ReactElement => {
    const { data: carriersData } = useQuery<GetCarrier>(GET_CARRIERS);
    const { data: regionsData } = useQuery<GetAllRegions>(GET_ALL_REGIONS);

    const { data, loading } = useQuery<GetAllDriversMasterData>(GET_ALL_DRIVERS_MASTER_DATA, {
        variables: {
            input: {
                regionIds: [],
                districtIds: [],
                statuses: [],
                carrierIds: [],
                search: '',
                driverId: driverId,
            },
        },
        fetchPolicy: 'network-only',
        skip: driverId == 0,
    });

    let selectedDriver: Driver | null | undefined;

    if (driverId !== 0 && data && data.getAllDriversMasterData && data.getAllDriversMasterData.length !== 0) {
        selectedDriver = data.getAllDriversMasterData[0];
    }

    const canEditDriverSchedule = useAuthContext().userHasPermission(PermissionEnum.DriverSchedule);
    const canEditSBXDrivers = useAuthContext().userHasPermission(PermissionEnum.EditDriver);
    const canEditThirdPartyDrivers = useAuthContext().userHasPermission(PermissionEnum.Edit3rdPartyCarrier);
    const canEditCellPhone = useAuthContext().userHasPermission(PermissionEnum.EditDriverCellNumber);
    const canEditDriverDistrict = useAuthContext().userHasPermission(PermissionEnum.EditDriverDistrict);
    const canEditDriverRegion = useAuthContext().userHasPermission(PermissionEnum.EditDriverRegion);
    const canEditKronosId = useAuthContext().userHasPermission(PermissionEnum.EditKronosID);

    const origCarriers: CarrierOption[] = [];
    const carriers: Option[] = [];
    const regions: OptionType[] = [];
    const driverCompanyReference: DriverCompanies[] = [];
    const initialDistrictsnew: OptionType[] = [];
    const initialSelectedRegions: OptionType[] = [];
    const initialSelectedDistricts: OptionType[] = [];

    const [selectedId, setSelectedId] = useState(0);
    const [selectedCarrierId, setSelectedCarrierId] = useState(0);
    const [selectedDriverName, setSelectedDriverName] = useState('');
    const [selectedCellPhone, setSelectedCellPhone] = useState('');
    const [selectedCDLNumber, setSelectedCDLNumber] = useState('');
    const [selectedCDLExpiryDate, setSelectedCDLExpiryDate] = useState(new Date());
    const [selectedCompanyName, setSelectedCompanyName] = useState('');
    const [selectedCompanyType, setSelectedCompanyType] = useState('');
    const [selectedExtSystemIdentifier3, setSelectedExtSystemIdentifier3] = useState('');

    const [selectedKronosId, setSelectedKronosId] = useState('');
    const [selectedIsActive, setSelectedIsActive] = useState(true);
    const [selectedIsAssigned, setSelectedIsAssigned] = useState(false);
    const [selectedIsManual, setSelectedIsManual] = useState(true);
    const [selectedIsAvailable, setSelectedIsAvailable] = useState(true);
    const [selectedIsShiftStarted, setSelectedIsShiftStarted] = useState(false);
    const [selectedIsLocked, setSelectedIsLocked] = useState(false);
    const [selectedExtSystemName, setSelectedExtSystemName] = useState('');
    const [selectedExtSystemIdentifier, setSelectedExtSystemIdentifier] = useState('');
    const [selectedInactiveReason, setSelectedInactiveReason] = useState('');
    const [selectedReactivationDate, setSelectedReactivationDate] = useState(new Date(''));
    const [selectedActiveInactiveDate, setSelectedActiveInactiveDate] = useState(new Date(''));
    const [selectedRegionIds, setSelectedRegionIds] = useState('');
    const [selectedDistrictIds, setSelectedDistrictIds] = useState('');
    const [selectedKronosByPass, setSelectedKronosByPass] = useState(false);
    const [selectedLoadId, setSelectedLoadId] = useState('');
    const [selectedLoadStatus, setSelectedLoadStatus] = useState('');
    const [currentAcronym, setCurrentAcronym] = useState('');

    const [selectedDistricts, setSelectedDistricts] = useState(initialSelectedDistricts);
    const [availableDistricts, setAvailableDistricts] = useState(initialDistrictsnew);
    const [selectedRegions, setSelectedRegions] = useState(initialSelectedRegions);

    const [cellPhoneError, setCellPhoneError] = useState('');

    const [showAlertPopup, setShowAlertPopup] = useState(false);
    const [modalType, setModalType] = useState('');

    enum modalTypes {
        Update = 'update',
        Unassign = 'unassign',
        EndShift = 'endShift',
    }

    const [
        getDuplicateDriver,
        { data: countDuplicates, loading: countDuplicatesLoading },
    ] = useLazyQuery<GetDriverByCellPhone>(GET_DRIVER_BY_CELLPHONE, { fetchPolicy: 'network-only' });

    const isEditForm = modalForm === ModalForm.EDIT_DRIVER;

    const variables = {
        driverId
    };
    
    const { data: driverCompanyReferenceData, loading: driverCompanyReferenceLoading } = useQuery<GetDriverCompanyReferenceByDriver>(
        GET_DRIVERCOMPANYREFERENCE,
        { variables, fetchPolicy: 'network-only', skip: driverId === 0 },
    );

    if (driverCompanyReferenceData?.getDriverCompanyReferenceByDriver) {
        driverCompanyReferenceData.getDriverCompanyReferenceByDriver.forEach((element: DriverCompanyReferenceType | null) => {
            if (!element || element.companyName === null || element.updatedDate === null || element.id === null || element.updatedBy === null) return;
            driverCompanyReference.push({
                id: element.id,
                companyName: element.companyName,
                updatedDate: element.updatedDate,
                updatedBy: element.updatedBy,
            });
        });
    }

    if (carriersData?.getCarriers) {
        carriersData.getCarriers.forEach((element: CarrierType | null) => {
            if (!element || element.companyName === null || element.id === null) return;
            origCarriers.push({
                id: element.id,
                companyName: element.companyName,
                companyType: element.companyType ?? '',
                acronym: element.acronym ?? '',
            });
            if (
                (canEditSBXDrivers && element.companyType === 'SBX') ||
                (canEditThirdPartyDrivers && element.companyType === 'CP') ||
                (canEditThirdPartyDrivers && element.companyType === 'LP')
            ) {
                carriers.push({ id: element.id, name: element.companyName });
            }
        });
    }

    if (regionsData && regionsData.getAllRegions) {
        regionsData.getAllRegions.forEach((element: RegionType | null) => {
            if (!element || element.regionName === null) return;
            regions.push({ value: element.id.toString(), label: element.regionName });
        });

        if (selectedDriver && selectedDriver.districtIds && selectedDriver.districtIds !== '') {
            const regionIds = selectedDriver.districtIds.split(',');

            const districtsListNew: OptionType[] = [];

            regionsData.getAllRegions.forEach((element: RegionType | null) => {
                if (!element || !regionIds.find(x => parseInt(x) === element.id) || !element.districts) return;
                element.districts.forEach((element1: DistrictType | null) => {
                    if (element1?.districtName != null) {
                        districtsListNew.push({ value: element1.id.toString(), label: element1.districtName });
                    }
                });
            });
        }
    }

    useEffect(() => {
        if (!countDuplicatesLoading) {
            if (countDuplicates && countDuplicates.getDriverByCellPhone) {
                if (countDuplicates.getDriverByCellPhone.length == 0) {
                    setCellPhoneError('');
                } else if (
                    countDuplicates.getDriverByCellPhone.length > 0 &&
                    selectedDriver &&
                    normalizeUsCellPhone(selectedCellPhone) === normalizeUsCellPhone(selectedDriver.cellPhone ?? '')
                ) {
                    setCellPhoneError('');
                } else {
                    setCellPhoneError('Cell phone number already exists.');
                }
            }
        }
    }, [countDuplicates]);

    const clearState = (): void => {
        setSelectedId(0);
        setSelectedDriverName('');
        setSelectedCellPhone('');
        setSelectedCDLNumber('');
        setSelectedCDLExpiryDate(new Date());
        setSelectedCompanyName('');
        setSelectedExtSystemIdentifier3('');
        setSelectedCompanyType('');
        setSelectedIsActive(true);
        setSelectedIsAssigned(false);
        setSelectedIsManual(true);
        setSelectedIsAvailable(true);
        setSelectedIsShiftStarted(false);
        setSelectedIsLocked(false);
        setSelectedRegions(initialSelectedRegions);
        setSelectedDistricts(initialSelectedDistricts);
        setSelectedCarrierId(0);
        setSelectedRegionIds('');
        setSelectedDistrictIds('');
        setSelectedKronosByPass(false);
        setSelectedReactivationDate(new Date(''));
        setCellPhoneError('');
        setSelectedKronosId('');
        setSelectedExtSystemName('');
        setSelectedExtSystemIdentifier('');
        setCurrentAcronym('');
    };

    useEffect(() => {
        if (show && selectedDriver) {
            setSelectedId(selectedDriver.driverId ? selectedDriver.driverId : 0);
            setSelectedCarrierId(selectedDriver.driverCompanyId ? selectedDriver.driverCompanyId : 0);
            setSelectedDriverName(selectedDriver.name ? selectedDriver.name : '');
            const loadedPhone = selectedDriver.cellPhone ? selectedDriver.cellPhone : '';
            setSelectedCellPhone(normalizeUsCellPhone(loadedPhone) ?? loadedPhone);
            setSelectedCDLNumber(selectedDriver.cdlNumber ? selectedDriver.cdlNumber : '');
            setSelectedExtSystemIdentifier3(
                selectedDriver.extSystemIdentifier3 ? selectedDriver.extSystemIdentifier3 : '',
            );
            setSelectedCDLExpiryDate(
                selectedDriver.cdlExpiryDate ? new Date(selectedDriver.cdlExpiryDate) : new Date(),
            );
            setSelectedCompanyName(selectedDriver.companyName ? selectedDriver.companyName : '');
            setSelectedCompanyType(selectedDriver.driverType ? selectedDriver.driverType : '');
            setSelectedIsActive(selectedDriver.isActive ? selectedDriver.isActive : false);
            if (selectedDriver?.loadId !== null || selectedDriver.isAssigned) setSelectedIsAssigned(true);
            else setSelectedIsAssigned(false);

            setSelectedIsManual(selectedDriver.isManual ? Boolean(selectedDriver.isManual) : false);
            setSelectedIsAvailable(selectedDriver.isAvailable ? Boolean(selectedDriver.isAvailable) : false);
            setSelectedIsShiftStarted(selectedDriver.isShiftStarted ? Boolean(selectedDriver.isShiftStarted) : false);
            setSelectedKronosId(selectedDriver.kronosId ? selectedDriver.kronosId : '');
            setSelectedIsLocked(selectedDriver.isLocked ? selectedDriver.isLocked : false);
            setSelectedExtSystemName(selectedDriver.extSystemName ? selectedDriver.extSystemName : '');
            setSelectedExtSystemIdentifier(
                selectedDriver.extSystemIdentifier ? selectedDriver.extSystemIdentifier : '',
            );
            setSelectedInactiveReason(selectedDriver.inactiveReason ? selectedDriver.inactiveReason : '');
            setSelectedReactivationDate(
                selectedDriver.reactivationDate ? new Date(selectedDriver.reactivationDate) : new Date(''),
            );

            setSelectedRegionIds(selectedDriver.regionIds ? selectedDriver.regionIds : '');
            setSelectedDistrictIds(selectedDriver.districtIds ? selectedDriver.districtIds : '');
            setSelectedLoadId(selectedDriver.loadId ? selectedDriver.loadId : '');
            setSelectedLoadStatus(selectedDriver.status ? selectedDriver.status : '');

            // Determine the currently-applied carrier acronym suffix (if any) so that
            // a subsequent carrier change can strip/replace it cleanly.
            const loadedCarrier = carriersData?.getCarriers?.find(
                (c): c is CarrierType => !!c && c.id === selectedDriver.driverCompanyId,
            );
            const loadedAcronym = (loadedCarrier?.acronym ?? '').trim().toUpperCase();
            const loadedName = (selectedDriver.name ?? '').trim().toUpperCase();
            if (loadedAcronym.length > 0 && loadedName.endsWith(` ${loadedAcronym}`)) {
                setCurrentAcronym(loadedAcronym);
            } else {
                setCurrentAcronym('');
            }

            if (selectedDriver.regionIds && selectedDriver.regionIds !== '') {
                const regionlist = selectedDriver.regionIds.split(',');
                const tmpRegions: OptionType[] = [];
                const tmpDistricts: OptionType[] = [];
                for (let i = 0; i < regionlist.length; i++) {
                    if (regions.find(l => l.value === regionlist[i]))
                        var tmpRegion = regions.find(l => l.value === regionlist[i]);

                    if (tmpRegion && tmpRegion.label !== null)
                        tmpRegions.push({
                            value: regionlist[i],
                            label: tmpRegion.label,
                        });

                    regionsData?.getAllRegions?.forEach((element: RegionType | null) => {
                        if (!element || parseInt(regionlist[i]) !== element.id || !element.districts) return;
                        if (selectedDriver?.districtIds && selectedDriver?.districtIds !== '') {
                            const districtlist = selectedDriver.districtIds.split(',');
                            for (let d = 0; d < districtlist.length; d++) {
                                if (element.districts.find(ed => ed?.id === parseInt(districtlist[d]))) {
                                    const tmpDistrict = element.districts.find(ed => ed?.id === parseInt(districtlist[d]));
                                    if (tmpDistrict?.districtName) {
                                        tmpDistricts.push({ value: districtlist[d], label: tmpDistrict.districtName });
                                    }
                                }
                            }
                        }
                    });
                }
                setSelectedRegions(tmpRegions);
                setSelectedDistricts(tmpDistricts);
            }
        }

        if ((show && !selectedDriver) || !show) {
            clearState();
        }
    }, [selectedDriver, show]);

    const checkForCellPhoneDuplicates = (): void => {
        const cell = normalizeUsCellPhone(selectedCellPhone) ?? selectedCellPhone;
        getDuplicateDriver({ variables: { input: { cellPhone: cell } } });
    };

    const onSave = (): void => {
        onClickSave(
            selectedId,
            selectedDriverName,
            normalizeUsCellPhone(selectedCellPhone) ?? selectedCellPhone,
            selectedCDLNumber,
            selectedCDLExpiryDate,
            selectedKronosId,
            selectedIsActive,
            selectedIsAssigned,
            selectedIsManual,
            selectedIsAvailable,
            selectedIsShiftStarted,
            selectedIsLocked,
            selectedCarrierId,
            selectedExtSystemName,
            selectedExtSystemIdentifier,
            selectedActiveInactiveDate,
            selectedInactiveReason,
            selectedReactivationDate,
            selectedRegionIds,
            selectedDistrictIds,
            selectedExtSystemIdentifier3.trimStart().trimEnd(),
        );
       // clearState();
    };

    const onCancel = (): void => {
        clearState();
        onClickHide();
    };

    const validateCellPhone = (phone: string): boolean => {
        return normalizeUsCellPhone(phone) !== null;
    };

    let ReadOnly = false;

    if (selectedCompanyType === 'SBX' && canEditSBXDrivers !== true) ReadOnly = true;
    if (selectedCompanyType === 'CP' && canEditThirdPartyDrivers !== true) ReadOnly = true;

    const setSaveButtonStatus = (): boolean => {
        const isCarrierValid = selectedCompanyName.trim().length > 0 ? true : false;
        const isNameValid = selectedDriverName.trim().length > 2 ? true : false;
        const checkCellPhoneExists = selectedCellPhone.length == 0 ? false : true;
        const isCellPhoneValid = validateCellPhone(selectedCellPhone) && cellPhoneError == '';
        const isCDLNumberValid = selectedCDLNumber.trim().length > 0 ? true : false;

        const isCDLExpiryValid =
            Object.prototype.toString.call(selectedCDLExpiryDate) === '[object Object]' ||
            Object.prototype.toString.call(selectedCDLExpiryDate) === '[object Date]'
                ? true
                : false;
        let isKronosIdValid = true;
        if (selectedCompanyType === 'SBX' && !selectedKronosByPass) {
            isKronosIdValid = selectedExtSystemIdentifier.trim().length > 0 ? true : false;
        }
        let hasPermission = false;
        if (selectedCompanyType == 'SBX' && canEditSBXDrivers) hasPermission = true;
        if (selectedCompanyType != 'SBX' && canEditThirdPartyDrivers) hasPermission = true;
        if (canEditDriverSchedule || canEditCellPhone || canEditDriverDistrict || canEditDriverRegion)
            hasPermission = true;

        let isActiveValid = true;
        if (selectedIsActive == false && selectedInactiveReason.length <= 0) isActiveValid = false;

        const isFormValid =
            isCarrierValid &&
            isNameValid &&
            checkCellPhoneExists &&
            isCellPhoneValid &&
            isCDLNumberValid &&
            isKronosIdValid &&
            hasPermission &&
            isCDLExpiryValid &&
            isActiveValid;

        return !isFormValid;
    };

    const changeExtSystemIdentifier = (event: any): void => {
        setSelectedExtSystemIdentifier(event.target.value);
    };

    const changeDriverName = (event: any): void => {
        setSelectedDriverName(event.target.value);
    };

    const onDriverNameBlur = (): void => {
        if (currentAcronym.length === 0) return;
        const trimmed = selectedDriverName.trim();
        if (trimmed.length === 0) return;

        // Already ends with the current carrier acronym — nothing to do.
        if (trimmed.toUpperCase().endsWith(` ${currentAcronym}`)) {
            if (trimmed !== selectedDriverName) setSelectedDriverName(trimmed);
            return;
        }

        // If the last token already looks like an acronym (2-6 alphanumerics, all upper),
        // treat it as the user's intentional edit and adopt it as the current acronym
        // instead of forcing the carrier's acronym back on.
        const parts = trimmed.split(/\s+/);
        const lastToken = parts[parts.length - 1] ?? '';
        if (parts.length > 1 && /^[A-Z0-9]{2,6}$/.test(lastToken)) {
            setCurrentAcronym(lastToken.toUpperCase());
            if (trimmed !== selectedDriverName) setSelectedDriverName(trimmed);
            return;
        }

        // Otherwise, append the carrier acronym to the base name.
        setSelectedDriverName(`${trimmed} ${currentAcronym}`);
    };

    const changeCellPhone = (event: any): void => {
        const raw = event.target.value ?? '';
        setCellPhoneError('');
        const normalized = normalizeUsCellPhone(raw);
        setSelectedCellPhone(normalized !== null ? normalized : raw);
    };

    const handleCellPhonePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
        const text = e.clipboardData.getData('text/plain');
        const normalized = normalizeUsCellPhone(text);
        if (normalized) {
            e.preventDefault();
            setSelectedCellPhone(normalized);
            setCellPhoneError('');
        }
    };

    const changeCDLNumber = (event: any): void => {
        setSelectedCDLNumber(event.target.value);
    };

    const changeReason = (event: any): void => {
        setSelectedInactiveReason(event.target.value);
    };

    const changeExtSystemIdentifier3 = (event: any): void => {
        setSelectedExtSystemIdentifier3(event.target.value);
    };

    const changeCarrier = (value: any): void => {
        const id = parseInt(value);
        setSelectedCarrierId(id);

        const carrier = origCarriers.find((e) => e.id === id);
        if (carrier !== undefined) {
            if (id != 0 && id != selectedDriver?.driverCompanyId && selectedDriver?.isAssigned) {
                setModalType(modalTypes.Update);
                setShowAlertPopup(true);
            }
            setSelectedCompanyType(carrier.companyType);
            setSelectedCompanyName(carrier.companyName);

            const newAcronym = (carrier.acronym ?? '').trim().toUpperCase();
            // Detect the acronym currently attached to the name (which may be a
            // manually-edited value the user just typed, not the tracked one).
            const effectiveOldAcronym = detectTrailingAcronym(selectedDriverName, currentAcronym);
            const updatedName = replaceDriverNameSuffix(selectedDriverName, effectiveOldAcronym, newAcronym);
            setSelectedDriverName(updatedName);
            setCurrentAcronym(newAcronym);
        }
    };

    useEffect(() => {
        if (selectedRegionIds) {
            getAvailableDistricts(selectedRegionIds.split(',').map(Number));
        }
    }, [selectedRegionIds]);

    const getAvailableDistricts = (regionId: number[]): void => {
        const districtsListNew: OptionType[] = [];
        const currentSelectedDistricts: OptionType[] = [];

        setAvailableDistricts(districtsListNew);
        regionsData?.getAllRegions?.forEach((element: RegionType | null) => {
            if (!element || !regionId.find(x => x === element.id) || !element.districts) return;
            element.districts.forEach((element1: DistrictType | null) => {
                if (element1?.districtName != null) {
                    districtsListNew.push({ value: element1.id.toString(), label: element1.districtName });
                }
            });
        });
        setAvailableDistricts(districtsListNew);

        if (selectedDistricts && selectedDistricts !== null && Array.isArray(selectedDistricts)) {
            for (let i = 0; i < selectedDistricts.length; i++) {
                if (selectedDistricts[i] && selectedDistricts[i].value !== null) {
                    var tmp = selectedDistricts[i].value;
                    if (districtsListNew.find(x => x.value === tmp)) {
                        currentSelectedDistricts.push(selectedDistricts[i]);
                    }
                }
            }
        }
        setSelectedDistricts(currentSelectedDistricts);
    };

    const onRegionChange = (selectedRegion: OptionType): void => {
        const regionIds: number[] = [];
        if (selectedRegion && selectedRegion !== null && Array.isArray(selectedRegion)) {
            for (let i = 0; i < selectedRegion.length; i++) {
                regionIds.push(parseInt(selectedRegion[i].value));
            }

            setSelectedRegions(selectedRegion);
        }
        getAvailableDistricts(regionIds);
        setSelectedRegionIds(regionIds.toString());
        if (selectedDistricts && selectedDistricts.length > 0) {
            const updatedDistricts = selectedDistricts.filter((district) => {
                return (regionsData?.getAllRegions ?? []).some(
                    (region) =>
                        region != null &&
                        regionIds.includes(region.id) &&
                        (region.districts ?? []).some((d) => d != null && d.id.toString() === district.value),
                );
            });
            setSelectedDistrictIds(updatedDistricts.map((d) => d.value).join(','));
        }
    };

    const onDistrictChange = (selectedDistrict: OptionType): void => {
        const districtIds: number[] = [];
        if (selectedDistrict && selectedDistrict !== null && Array.isArray(selectedDistrict)) {
            setSelectedDistricts(selectedDistrict);
            for (let i = 0; i < selectedDistrict.length; i++) {
                districtIds.push(parseInt(selectedDistrict[i].value));
            }
        }
        setSelectedDistrictIds(districtIds.toString());
    };

    const changeActive = (event: any): void => {
        const boolValue = JSON.parse(event.target.value);

        setSelectedIsActive(boolValue);
        setSelectedActiveInactiveDate(moment().toDate());
    };

    const changeAssigned = (event: any): void => {
        const boolValue = JSON.parse(event.target.value);

        setSelectedIsAssigned(boolValue);
    };
    const changeUnAssigned = (event: any): void => {
        if (selectedLoadId != '') {
            if (
                selectedLoadStatus == 'Assigned' ||
                selectedLoadStatus == 'Loaded' ||
                selectedLoadStatus == 'Loading' ||
                selectedLoadStatus == 'On-site'
            ) {
                setModalType(modalTypes.Unassign);
                setShowAlertPopup(true);
            } else {
                const boolValue = JSON.parse(event.target.value);
                setSelectedIsAssigned(boolValue);
            }
        } else {
            const boolValue = JSON.parse(event.target.value);
            setSelectedIsAssigned(boolValue);
        }
    };

    const changeSchedule = (event: any): void => {
        const boolValue = JSON.parse(event.target.value);

        setSelectedIsManual(boolValue);
    };

    const changeShiftStarted = (event: any): void => {
        if (selectedLoadId != '') {
            if (
                selectedLoadStatus == 'Assigned' ||
                selectedLoadStatus == 'Loaded' ||
                selectedLoadStatus == 'Loading' ||
                selectedLoadStatus == 'On-site'
            ) {
                setModalType(modalTypes.EndShift);
                setShowAlertPopup(true);
            } else {
                const boolValue = JSON.parse(event.target.value);
                setSelectedIsShiftStarted(boolValue);
            }
        } else {
            const boolValue = JSON.parse(event.target.value);
            setSelectedIsShiftStarted(boolValue);
        }
    };

    const changeLocked = (event: any): void => {
        const boolValue = JSON.parse(event.target.value);

        setSelectedIsLocked(boolValue);
    };

    const handleDateInputChange = (inputName: string, value: Date): void => {
        switch (inputName) {
            case 'cdlExpiryDate': {
                setSelectedCDLExpiryDate(value);
                break;
            }
            case 'reactivationDate': {
                setSelectedReactivationDate(value);
                break;
            }
            default: {
                //statements;
                break;
            }
        }
    };
    const searchIcon = <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} />;

    if (loading || driverCompanyReferenceLoading) {
        return (
            <div className="editDriverModalSpinner">
                <Loading />
            </div>
        );
    }

    return (
        <Modal show={show} onHide={onCancel} size="lg">
            <Modal.Header className="AddEditDriverModal-Header">
                <Modal.Title className="AddEditDriverModal-Header-Container">
                    <button className="material-icons AddEditDriverModal-X-Button" onClick={onCancel}>
                        close
                    </button>
                    <div className="AddEditDriverModal-Title">{isEditForm ? 'Edit Driver' : 'Create New Driver'}</div>
                    <div className="AddEditDriverModal-ErrorMessage">{errorMessage}</div>
                </Modal.Title>
            </Modal.Header>
            <div className="AddEditDriverModal-Container">
                <Row className="AddEditDriverModal-Upper-TextField-Container">
                    <Col md={4} className="AddEditDriverModal-Textfield">
                        <SearchableDropdown
                            autoCompleteEnabled={true}
                            dropdownOptions={carriers}
                            id="carrier"
                            label="*Carrier"
                            onSelect={(eventKey: string | null) => {
                                changeCarrier(eventKey);
                            }}
                            value={selectedCompanyName}
                            disabled={ReadOnly}
                            endIcon={searchIcon}
                        />
                    </Col>
                    <Col md={4} className="AddEditDriverModal-Textfield">
                        <Textfield label="* Driver Company Type" disabled={true} value={selectedCompanyType} />
                    </Col>
                    <Col md={4} className="AddEditDriverModal-Textfield">
                        <Textfield
                            label="Driver ISNET ID"
                            value={selectedExtSystemIdentifier3}
                            onChange={(event): void => changeExtSystemIdentifier3(event)}
                        />
                    </Col>
                </Row>
                <Row className="AddEditDriverModal-Upper-TextField-Container">
                    <Col md={6} className="AddEditDriverModal-Container">
                        <div className={isEditForm && (!isEditForm || !canEditDriverRegion) ? 'disabled' : ''}>
                            <MultiSelectDropdownV2
                                dropdownOptions={regions}
                                label="Region"
                                onChange={(value: OptionType): void => {
                                    onRegionChange(value);
                                }}
                                key={`my_unique_select_key__${JSON.stringify(selectedRegions)}`}
                                value={selectedRegions}
                            />
                        </div>
                    </Col>
                    <Col md={6} className="AddEditDriverModal-Container">
                        <div className={isEditForm && (!isEditForm || !canEditDriverDistrict) ? 'disabled' : ''}>
                            <MultiSelectDropdownV2
                                dropdownOptions={availableDistricts}
                                label="Districts"
                                onChange={(value: OptionType): void => {
                                    onDistrictChange(value);
                                }}
                                key={`my_unique_select_key__${JSON.stringify(selectedRegions)}`}
                                value={selectedDistricts}
                            />
                        </div>
                    </Col>
                </Row>
                <Row className="AddEditDriverModal-Upper-TextField-Container">
                    <Col md={3} className="AddEditDriverModal-Textfield">
                        <Textfield
                            label="*Driver Name"
                            value={selectedDriverName}
                            onChange={(event): void => changeDriverName(event)}
                            onBlur={onDriverNameBlur}
                            disabled={ReadOnly}
                        />
                    </Col>
                    <Col md={3} className="AddEditDriverModal-Textfield">
                        <Textfield
                            label="*Cell Phone"
                            value={selectedCellPhone}
                            onChange={(event): void => changeCellPhone(event)}
                            onPaste={handleCellPhonePaste}
                            onBlur={() => {
                                checkForCellPhoneDuplicates();
                            }}
                            mask={'999-999-9999'}
                            disabled={!canEditCellPhone && ReadOnly}
                            error={cellPhoneError !== ''}
                            helperText={cellPhoneError}
                        />
                    </Col>
                    <Col md={3} className="AddEditDriverModal-Textfield">
                        <Textfield
                            label="*CDL Number"
                            value={selectedDriver !== undefined ? selectedDriver?.cdlNumber : ''}
                            onChange={(event): void => changeCDLNumber(event)}
                            onBlur={() => {}}
                            disabled={ReadOnly}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col md={3} className="AddEditDriverModal-Textfield">
                        <div className={ReadOnly ? 'disabled' : ''}>
                            <DateTimePicker
                                dateOnly
                                label="*CDL Expiry Date"
                                value={
                                    isEditForm
                                        ? selectedDriver?.cdlExpiryDate
                                            ? selectedDriver?.cdlExpiryDate
                                            : ''
                                        : selectedCDLExpiryDate
                                }
                                onChange={(value: any) => {
                                    handleDateInputChange('cdlExpiryDate', value);
                                }}
                            />
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col md={3} className="AddEditDriverModal-Textfield">
                        <Textfield
                            label="*Kronos ID"
                            value={selectedDriver !== undefined ? selectedDriver?.kronosId : ''}
                            onChange={(event): void => changeExtSystemIdentifier(event)}
                            onBlur={() => {}}
                            //disabled={selectedCompanyType === 'SBX' ? false : true}
                            disabled={(ReadOnly && !canEditKronosId) || selectedCompanyType === 'CP'}
                        />
                    </Col>
                    <Col md={3} className="AddEditDriverModal-Textfield">
                        <input
                            type="checkbox"
                            checked={selectedKronosByPass}
                            className="form-check-input"
                            onChange={(event): void => {
                                setSelectedKronosByPass(event.currentTarget.checked);
                            }}
                            disabled={(ReadOnly && !canEditKronosId) || selectedCompanyType === 'CP'}
                        />
                        <label className="form-check-label">Kronos ByPass</label>
                    </Col>
                </Row>
                <Row>
                    <Col md={4} className="AddEditDriverModal-Radio">
                        <div className="AddEditDriver-InputTitle">Is Driver Active?</div>
                    </Col>
                    <Col md={2} className="AddEditDriverModal-Radio">
                        <input
                            className="AddEditDriverModal-Radio-Input"
                            type="radio"
                            value="true"
                            checked={selectedIsActive}
                            onChange={(event): void => changeActive(event)}
                            disabled={ReadOnly && !canEditDriverSchedule}
                        />
                        Active
                    </Col>
                    <Col md={2}>
                        <input
                            className="AddEditDriverModal-Radio-Input"
                            type="radio"
                            value="false"
                            checked={!selectedIsActive}
                            onChange={(event): void => changeActive(event)}
                            disabled={ReadOnly && !canEditDriverSchedule}
                        />
                        InActive
                    </Col>
                </Row>
                <div style={{ display: !selectedIsActive ? 'block' : 'none' }}>
                    <Row>
                        <Col md={3} className="AddEditDriverModal-Radio">
                            <DateTimePicker
                                dateOnly
                                label="Reactivation Date"
                                value={selectedDriver !== undefined ? selectedDriver?.reactivationDate : ''}
                                onChange={(value: any) => {
                                    handleDateInputChange('reactivationDate', value);
                                }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={3} className="AddEditDriverModal-Radio">
                            <Textfield
                                label="*Reason"
                                value={selectedDriver !== undefined ? selectedDriver?.inactiveReason : ''}
                                onChange={(event): void => changeReason(event)}
                            />
                        </Col>
                    </Row>
                </div>
                <Row>
                    <Col md={4} className="AddEditDriverModal-Radio">
                        <div className="AddEditDriver-InputTitle">Is Driver Assigned?</div>
                    </Col>
                    <Col md={2} className="AddEditDriverModal-Radio">
                        <input
                            className="AddEditDriverModal-Radio-Input"
                            type="radio"
                            value="true"
                            checked={selectedIsAssigned}
                            onChange={(event): void => changeAssigned(event)}
                            disabled={ReadOnly && !canEditDriverSchedule && isEditForm ? false : true}
                        />
                        Assigned
                    </Col>
                    <Col md={2}>
                        <input
                            className="AddEditDriverModal-Radio-Input"
                            type="radio"
                            value="false"
                            checked={!selectedIsAssigned}
                            onChange={(event): void => changeUnAssigned(event)}
                            disabled={ReadOnly && !canEditDriverSchedule}
                        />
                        UnAssigned
                    </Col>
                </Row>
                <Row>
                    <Col md={4} className="AddEditDriverModal-Radio">
                        <div className="AddEditDriver-InputTitle">Driver Schedule</div>
                    </Col>
                    <Col md={2} className="AddEditDriverModal-Radio">
                        <input
                            className="AddEditDriverModal-Radio-Input"
                            type="radio"
                            value="true"
                            checked={selectedIsManual}
                            onChange={(event): void => changeSchedule(event)}
                            disabled={ReadOnly && !canEditDriverSchedule}
                        />
                        Manual
                    </Col>
                    <Col md={2}>
                        <input
                            className="AddEditDriverModal-Radio-Input"
                            type="radio"
                            value="false"
                            checked={!selectedIsManual}
                            onChange={(event): void => changeSchedule(event)}
                            disabled={ReadOnly && !canEditDriverSchedule}
                        />
                        Scheduled
                    </Col>
                </Row>
                <Row>
                    <Col md={4} className="AddEditDriverModal-Radio">
                        <div className="AddEditDriver-InputTitle">Is Driver Checked In?</div>
                    </Col>
                    <Col md={2} className="AddEditDriverModal-Radio">
                        <input
                            className="AddEditDriverModal-Radio-Input"
                            type="radio"
                            value="true"
                            checked={selectedIsShiftStarted}
                            onChange={(event): void => changeShiftStarted(event)}
                            disabled={ReadOnly && !canEditDriverSchedule}
                        />
                        Check In
                    </Col>
                    <Col md={2}>
                        <input
                            className="AddEditDriverModal-Radio-Input"
                            type="radio"
                            value="false"
                            checked={!selectedIsShiftStarted}
                            onChange={(event): void => changeShiftStarted(event)}
                            disabled={ReadOnly && !canEditDriverSchedule}
                        />
                        Check Out
                    </Col>
                </Row>
                <Row>
                    <Col md={4} className="AddEditDriverModal-Radio">
                        <div className="AddEditDriver-InputTitle">Is Driver Locked?</div>
                    </Col>
                    <Col md={2} className="AddEditDriverModal-Radio">
                        <input
                            className="AddEditDriverModal-Radio-Input"
                            type="radio"
                            value="true"
                            checked={selectedIsLocked}
                            onChange={(event): void => changeLocked(event)}
                            disabled={ReadOnly && !canEditDriverSchedule}
                        />
                        Locked
                    </Col>
                    <Col md={2}>
                        <input
                            className="AddEditDriverModal-Radio-Input"
                            type="radio"
                            value="false"
                            checked={!selectedIsLocked}
                            onChange={(event): void => changeLocked(event)}
                            disabled={ReadOnly && !canEditDriverSchedule}
                        />
                        UnLocked
                    </Col>
                </Row>
                                <Row>
                    <Col md={4} className="AddEditDriverModal-Radio">
                        <div className="AddEditDriver-InputTitle">DNR</div>
                    </Col>
                    <Col md={4} className="AddEditDriverModal-Toggle">
                        {/* DNR (Do Not Run) is the inverse of IsAvailable: DNR on => IsAvailable = false. */}
                        <Form.Check
                            type="switch"
                            id="dnr-toggle"
                            checked={!selectedIsAvailable}
                            onChange={(event): void =>
                                setSelectedIsAvailable(!event.currentTarget.checked)
                            }
                            disabled={ReadOnly && !canEditDriverSchedule}
                            label={selectedIsAvailable ? 'Off' : 'On'}
                        />
                    </Col>
                </Row>
                <div>
                    <div className="DriverCompany-Header-Item justify-content-start">Driver Company History</div>
                    <div className="Row">
                        <div className="DriverCompany-Header-Item justify-content-start">Company Name</div>
                        <div className="DriverCompany-Header-Item justify-content-start">Updated Date</div>
                        <div className="DriverCompany-Header-Item justify-content-start">Updated By</div>
                    </div>
                    <div>
                        {driverCompanyReference &&
                            driverCompanyReference.map((driverCompany: DriverCompanies): React.ReactElement | undefined => {
                                if (driverCompany !== null) {
                                    return (
                                        <DriverCompanyReferenceItem
                                            id={driverCompany.id}
                                            key={`driverComp${driverCompany.id}`}
                                            companyName={driverCompany.companyName}
                                            updatedDate={driverCompany.updatedDate}
                                            updatedBy={driverCompany.updatedBy}
                                        />
                                    );
                                }

                                return undefined;
                            })}
                    </div>
                </div>
            </div>

            <Modal.Footer className="AddEditUserModal-Footer">
                <CustomButton title={'Cancel'} isBordered onClick={onCancel}></CustomButton>
                <CustomButton title="Save" isDisabled={setSaveButtonStatus()} onClick={onSave}></CustomButton>
            </Modal.Footer>

            <UpdateDriverConfirmationModal
                show={showAlertPopup}
                loadId={selectedDriver?.loadId}
                modalType={modalType}
                onCancel={() => setShowAlertPopup(false)}
            />
        </Modal>
    );
};

export default AddEditDriverModal;

